// Agent-chat composer. Rebuilt on design-system primitives (Input, ButtonIcon)
// and the DS `-mfe-` chrome classes (W-24314366), replacing the retired
// tds2_shim/agentforce/InputBar. The DS AgentforceMFEModalInputBar is single-line
// only and lacks `disabled`, a `sendButton` override, and `multiline`, all of
// which the agent-chat composer needs — so this composes the same primitives and
// emits the same `-mfe-` classes (inheriting the DS base styling) while adding
// those three features.
//
// The `multiline` composer uses a raw <textarea> rather than the DS Textarea:
// the composer textarea is a borderless, transparent, auto-growing field inside a
// separately-bordered shell, whereas DS Textarea ships a bordered box with its own
// focus ring. Reusing it would mean overriding essentially all of its base styling,
// so a raw textarea (styled by `.tds2-agentforce__composer-textarea`) is the honest
// fit. The composer chrome has no DS equivalent and lives in _agent_chat.scss.
import React from 'react';
import PropTypes from 'prop-types';
import { Input, ButtonIcon } from '@trailhead-emu/design-system';
import AgentforceIcon from './AgentforceIcon';

const MAX_HEIGHT_PX = 12 * 16; // 12rem cap, matches the prototype composer.

let inputIdCounter = 0;
function generateInputId() {
  inputIdCounter += 1;
  return 'agent-chat-input-' + inputIdCounter;
}

const SendIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M22.9,11.4c-.1-.3-.4-.6-.7-.7L4.1,2.1c-.4-.2-.8-.2-1.1,0-.4.1-.7.4-.9.8-.2.4-.2.8,0,1.2l2.4,6.3c.1.4.5.7.9.7h8.6c.6,0,1,.4,1,1s-.4,1-1,1H5.4c-.4,0-.8.3-.9.7l-2.4,6.3c-.1.4-.1.8,0,1.1.2.4.5.6.8.8.2,0,.4.1.5.1s.4,0,.6-.1l18-8.5c.4-.2.6-.5.8-.9.1-.4.1-.8,0-1.1Z" />
  </svg>
);

export default class AgentChatInputBar extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    placeholder: PropTypes.string,
    value: PropTypes.string,
    defaultValue: PropTypes.string,
    onValueChange: PropTypes.func,
    onSend: PropTypes.func,
    inputId: PropTypes.string,
    disableSendUnlessNonempty: PropTypes.bool,
    hideDisclaimer: PropTypes.bool,
    disabled: PropTypes.bool,
    sendButton: PropTypes.node,
    multiline: PropTypes.bool,
  };

  static defaultProps = {
    placeholder: 'Message Agentforce',
    disableSendUnlessNonempty: false,
    hideDisclaimer: false,
    disabled: false,
    multiline: false,
  };

  constructor(props) {
    super(props);
    this.state = { internalValue: props.defaultValue || '' };
    this.inputId = props.inputId || generateInputId();
    this.textareaRef = React.createRef();
  }

  componentDidMount() {
    if (this.props.multiline) {
      this.resizeTextarea();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.multiline) {
      const value = this.currentValue();
      if (value !== this.previousValueForResize || !prevProps.multiline) {
        this.resizeTextarea();
      }
    }
  }

  isControlled() {
    return this.props.value !== undefined;
  }

  currentValue() {
    return this.isControlled() ? this.props.value : this.state.internalValue;
  }

  resizeTextarea() {
    const el = this.textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, MAX_HEIGHT_PX) + 'px';
    this.previousValueForResize = this.currentValue();
  }

  setValue(next) {
    if (!this.isControlled()) {
      this.setState({ internalValue: next });
    }
    if (this.props.onValueChange) {
      this.props.onValueChange(next);
    }
  }

  handleChange = (e) => {
    this.setValue(e.target.value);
  };

  handleClear = () => {
    this.setValue('');
  };

  handleSend = () => {
    const value = this.currentValue();
    if (this.props.disableSendUnlessNonempty && !value.trim()) return;
    if (this.props.onSend) {
      this.props.onSend(value);
    }
  };

  handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.handleSend();
    }
  };

  renderClearButton() {
    return (
      <ButtonIcon
        ariaLabel="Clear message"
        iconSymbol="close"
        variant="bare"
        size="extra-small"
        onClick={this.handleClear}
        className="tds2-agentforce-mfe__clear-button"
      />
    );
  }

  renderSendButton(canSend) {
    return (
      <ButtonIcon
        ariaLabel={canSend ? 'Send message' : 'Send message — type a message first'}
        variant="primary"
        size="extra-small"
        type="button"
        disabled={!canSend || this.props.disabled}
        onClick={this.handleSend}
        className="tds2-agentforce-mfe__send-button"
      >
        <SendIcon />
      </ButtonIcon>
    );
  }

  renderDisclaimer() {
    if (this.props.hideDisclaimer) return null;
    return (
      <p className="tds2-agentforce-mfe-modal__input-disclaimer">
        <strong>This feature is in beta.</strong> AI-generated content may contain errors.
      </p>
    );
  }

  renderSingleLine(value, hasContent, canSend) {
    const { placeholder, disabled, sendButton } = this.props;
    return (
      <div className="tds2-agentforce-mfe__prompt-row">
        <div className="tds2-agentforce-mfe__avatar" aria-hidden="true">
          <AgentforceIcon />
        </div>
        <Input
          id={this.inputId}
          type="text"
          className="tds2-agentforce-mfe__prompt-input"
          value={value}
          onChange={this.handleChange}
          onKeyDown={this.handleKeyDown}
          placeholder={placeholder}
          aria-label={placeholder}
          disabled={disabled}
        />
        {hasContent && !disabled && this.renderClearButton()}
        {sendButton || this.renderSendButton(canSend)}
      </div>
    );
  }

  renderMultiline(value, hasContent, canSend) {
    const { placeholder, disabled, sendButton } = this.props;
    const composerClass = hasContent
      ? 'tds2-agentforce__composer-shell tds2-agentforce__composer-shell--has-content'
      : 'tds2-agentforce__composer-shell';

    return (
      <div className={composerClass}>
        <div className="tds2-agentforce__composer-row">
          <textarea
            ref={this.textareaRef}
            id={this.inputId}
            className="tds2-agentforce__composer-textarea"
            value={value}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            placeholder={placeholder}
            aria-label={placeholder}
            rows={1}
            autoComplete="off"
            disabled={disabled}
          />
        </div>
        <div className="tds2-agentforce__composer-actions">
          {hasContent && !disabled && this.renderClearButton()}
          {sendButton || this.renderSendButton(canSend)}
        </div>
      </div>
    );
  }

  render() {
    const { className, disableSendUnlessNonempty, multiline } = this.props;
    const value = this.currentValue();
    const hasContent = value.trim().length > 0;
    const canSend = !disableSendUnlessNonempty || hasContent;
    const dockClasses = ['tds2-agentforce-mfe-modal__input-dock'];

    if (multiline) dockClasses.push('tds2-agentforce-mfe-modal__input-dock--multiline');
    if (className) dockClasses.push(className);

    return (
      <div className={dockClasses.join(' ')} data-th-component="AgentChatInputBar">
        <div className="tds2-agentforce-mfe-modal__input-stack">
          {multiline
            ? this.renderMultiline(value, hasContent, canSend)
            : this.renderSingleLine(value, hasContent, canSend)}
          {this.renderDisclaimer()}
        </div>
      </div>
    );
  }
}
